// Shapes returned and submitted by the v1 house_keeping API.
export namespace HouseKeeping {

  export interface Option<T = number | string> {
    id: T;
    name: string;
  }

  export interface FileRef {
    url?: string | null;
  }

  export type FieldValue = string | number | boolean | Date | null;

  export interface GroupSelection {
    group_id?: number | string | null;
    group_ids?: Array<number | string | null>;
  }

  export function isGroupSelection(value: unknown): value is GroupSelection {
    return !!value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
  }

  // value of a table filter, or of a query param built from it
  export type FilterValue =
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | GroupSelection
    | FilterValue[];

  export interface SelectedFilters {
    [key: string]: FilterValue;
  }

  // `assigned` is the house keeping keeper or the plan's user.
  // `finalizer` is the events archive column: who finalized the event.
  export type ResponsibleFilterKind = 'assigned' | 'finalizer';

  // `responsibles_source` ids are `user_id::<id>` / `service_provider_id::<id>`
  // (and sometimes `keeper_id::<id>`). Replace `responsible` with the scalar
  // params each index endpoint filters on, so every page decodes the same token.
  export function applyResponsibleFilter(filters: SelectedFilters, kind: ResponsibleFilterKind = 'assigned'): void {
    const responsible = filters.responsible;
    delete filters.responsible;
    if (
      responsible == null
      || responsible === ''
      || typeof responsible === 'boolean'
      || responsible instanceof Date
      || Array.isArray(responsible)
      || isGroupSelection(responsible)
    ) {
      return;
    }

    const encoded = String(responsible);
    const separator = encoded.indexOf('::');
    const field = separator === -1 ? 'user_id' : encoded.slice(0, separator);
    const id = separator === -1 ? encoded : encoded.slice(separator + 2);
    if (!id) {
      return;
    }

    const normalized = field === 'keeper_id' ? 'user_id' : field;
    if (kind === 'finalizer') {
      if (normalized === 'user_id' || normalized === 'created_by') {
        filters.created_by = id;
      } else if (normalized === 'service_provider_id') {
        filters.service_provider_id = id;
      }
      return;
    }

    if (normalized === 'service_provider_id') {
      filters.service_provider_id = id;
    } else if (normalized === 'user_id') {
      filters.user_id = id;
    }
  }

  export interface ListMeta {
    per_page: number;
    total_records: number;
    total_pages: number;
  }

  export interface ApiFailure {
    errors?: unknown;
    error?: unknown;
    message?: unknown;
  }

  export function asApiFailure(value: unknown): ApiFailure | null {
    if (value && typeof value === 'object') {
      return value as ApiFailure;
    }
    return null;
  }

  // GET /api/v1/house_keeping/house_keepings/available_filters
  // and the sibling plans / events filter endpoints
  export interface AvailableFilters {
    documents_source?: Option[];
    groups_source?: Option[];
    categories_source?: Option[];
    // ids are 'user_id::<id>' / 'service_provider_id::<id>'
    responsibles_source?: Option[];
    statuses_source?: Option<string>[];
    users_source?: Option[];
    house_keepings_source?: Option[];
    plan_statuses_source?: Option<string>[];
    repeatTypes?: Option<string>[];
  }

  export interface DocumentFiltersResponse {
    filters?: AvailableFilters;
  }

  // index + show

  export interface NamedRef {
    id: number;
    name: string;
  }

  export interface Responsible {
    type?: string;
    id: number;
    name: string;
  }

  // Resources::HouseKeepings::HouseKeeping#status
  export interface ImplementationStatus {
    type: 'progress' | 'complete' | 'empty' | string;
    value: number;
    implemented: number;
    total: number;
  }

  export interface Creator {
    id: number;
    name?: string;
    full_name?: string;
  }

  // Resources::HouseKeepings::HouseKeeping and HouseKeepingShow
  export interface Item {
    id: number;
    name: string;
    description?: string | null;
    label?: string;
    is_singleton?: boolean;
    is_service_provider_responsible?: boolean;
    category_id?: number | null;
    repeat_interval?: number | null;
    repeat_type?: string | null;
    document?: NamedRef | null;
    document_name?: string;
    document_id?: number | null;
    group?: NamedRef | null;
    group_name?: string;
    group_id?: number | null;
    category?: NamedRef | null;
    category_name?: string;
    responsible?: Responsible | null;
    recommended_measures?: number;
    status?: ImplementationStatus;
    next_due_on?: string | null;
    is_overdue?: boolean;
    overdue_class?: string | null;
    last_event_at?: string | null;
    last_not_ok_event_date?: string | null;
    created_at?: string;
    updated_at?: string;
    creator?: Creator | null;
    creator_name?: string;
    keeper_id?: number | null;
    service_provider_id?: number | null;
    protective_actions?: ProtectiveAction[];
    actions?: ProtectiveAction[];
    archive?: ArchiveEventSource[];
    events?: ArchiveEventSource[];
    archive_events?: ArchiveEventSource[];
  }

  export interface ItemsResponse {
    house_keepings?: Item[];
    house_keeping?: Item;
    data?: { house_keeping?: Item };
    meta?: ListMeta;
    id?: number;
  }

  // protective actions (V1::HouseKeepings::ShowProtectiveActions)

  export interface Image {
    thumb?: string | null;
    small_thumb?: string | null;
    full_image?: string | null;
  }

  export interface Comment {
    body?: string;
    is_active?: boolean;
    created_at?: string;
  }

  export interface ActionUser {
    id: number;
    name: string;
  }

  export interface ActionPlan {
    id: number;
    next_due_on?: string | null;
    last_event_at?: string | null;
    group_id?: number | null;
  }

  export interface ActionEvent {
    creator_name?: string | null;
    finalized_at?: string | null;
  }

  export interface ActionParty {
    user?: ActionUser | null;
    plan?: ActionPlan | null;
    event?: ActionEvent | null;
  }

  export interface ProtectiveAction {
    id: number;
    description?: string;
    risk_status?: string;
    status?: string;
    recommended?: boolean;
    not_implemented?: boolean;
    images?: Image[];
    comments?: Comment[];
    responsible?: ActionParty | null;
    checker?: ActionParty | null;
  }

  export interface ActionForm {
    risk_status: string;
    description: string;
    responsible_user_id: number | null;
    responsible_user_date: Date | string | null;
    include_checker: boolean;
    checker_id: number | null;
    checker_date: Date | string | null;
    picture: File | null;
  }

  export interface CameraPicture {
    name: string;
    finalized_image: string;
  }

  export interface ProtectiveActionPayload {
    description: string;
    risk_status: string;
    responsible_user_id?: number | null;
    responsible_user_date?: string | null;
    checker_id?: number | null;
    checker_date?: string | null;
    camera_pictures?: CameraPicture[];
  }

  // archive events (V1::HouseKeepings::ArchiveEventRow and Resources::HouseKeepings::Event)

  export interface ArchiveEventSource {
    id?: number;
    label?: string;
    name?: string;
    finalized_at?: string | null;
    document?: NamedRef | null;
    document_name?: string;
    document_id?: number | string | null;
    responsible?: string | null;
    responsible_name?: string;
    status?: string;
    status_label?: string;
    type?: string;
    type_label?: string;
    file?: string | null;
    file_url?: string | null;
    house_keeping?: NamedRef | null;
    house_keeping_name?: string;
    work_environment?: string;
  }

  export interface ArchiveEvent {
    id?: number;
    label: string;
    finalized_at?: string | null;
    document_name: string;
    document_id: number | string | null;
    responsible: string;
    status?: string;
    status_label: string;
    type_label: string;
    file_url: string | null;
    house_keeping?: NamedRef | null;
    house_keeping_name?: string;
    work_environment?: string;
  }

  export interface EventsResponse {
    events?: ArchiveEvent[];
    event?: ArchiveEvent;
    meta?: ListMeta;
  }

  export interface ArchiveFilters {
    description: string;
    finalized_at: Array<Date | string | null> | null;
    document_id: number | string | null;
    responsible: string | number | null;
    status: string | number | null;
  }

  export interface ArchiveFilterSources {
    documents_source: Option[];
    responsibles_source: Option[];
    statuses_source: Option[];
  }

  // plans (Resources::HouseKeepings::Plan)

  export interface PlanAction {
    id?: number;
    description?: string;
    comment?: string;
    has_checker_plan?: boolean;
    responsible_user_id?: number | null;
  }

  export interface Plan {
    id: number;
    user_id?: number | null;
    status?: string;
    kind?: string;
    group_id?: number | null;
    service_provider_id?: number | null;
    is_service_provider_responsible?: boolean;
    is_singleton?: boolean;
    repeat_interval?: number | null;
    repeat_type?: string | null;
    document_id?: number | null;
    house_keeping_id?: number | null;
    label?: string;
    name?: string;
    prefix?: string;
    house_keeping?: NamedRef | null;
    house_keeping_name?: string;
    document_name?: string;
    group_name?: string;
    responsible?: Responsible | null;
    next_due_on?: string | null;
    last_event_at?: string | null;
    last_event_status?: string | null;
    house_keeping_type?: string;
    cycle_plan?: string;
    protective_action?: PlanAction | null;
    is_overdue?: boolean;
    is_overdue_today?: boolean;
    overdue_class?: string | null;
    responsible_plan_not_done?: boolean;
  }

  export interface PlansResponse {
    plans?: Plan[];
    plan?: Plan;
    meta?: ListMeta;
    id?: number;
  }

  export interface RealizationForm {
    finalized_at: Date | string | null;
    comment: string;
  }

  export interface EffectivenessForm {
    // The checker form also accepts the Rails values 1 / '1' / 'true'.
    rejected: boolean | number | string;
    finalized_at: Date | string | null;
    comment: string;
    responsible_user_id: number | null;
    responsible_user_date: Date | string | null;
    add_checker: boolean;
    checker_id: number | null;
    checker_date: Date | string | null;
  }

  export interface FollowUpAction {
    risk_status: string;
    description: string;
    responsible_user_id: number | null;
    responsible_user_due_on: Date | string | null;
    checker_id: number | null;
    checker_due_on: Date | string | null;
  }

  export interface FollowUpPayload {
    description: string;
    risk_status: string;
    responsible_user_id: number | null;
    responsible_user_due_on: string | null;
    checker_id: number | null;
    checker_due_on: string | null;
  }

  export interface EventPayload {
    plan_id: number;
    group_id?: number | null;
    protective_action_id?: number | null;
    finalized_at?: string | null;
    finalized_language?: string;
    comment?: string;
    rejected?: '0' | '1';
    responsible_user_id?: number | null;
    responsible_user_date?: string | null;
    checker_id?: number | null;
    checker_date?: string | null;
    status?: string;
    back_end_signature?: boolean;
    protective_actions?: FollowUpPayload[];
    camera_pictures?: CameraPicture[];
    attached_file?: string;
    finalized_document?: string;
  }

  // sidebars

  export interface CountedEntity {
    id: number;
    name: string;
    total_counts: number;
    overdue_counts: number;
    due_today_counts: number;
  }

  export interface ExcelLink {
    text: string;
    hyperlink: string;
  }

  // writes

  export interface UpdatePayload {
    [key: string]: FieldValue | { name: string } | undefined;
    group_id?: number | string | null;
    keeper_id?: number | string | null;
    user_id?: number | string | null;
    service_provider_id?: number | null;
    is_service_provider_responsible?: boolean;
    is_singleton?: boolean;
    category_attributes?: { name: string };
  }

  export interface KeeperAssignment {
    keeper_id: number | string | null;
    service_provider_id: number | null;
    is_service_provider_responsible: boolean;
  }

  export interface PlanAssignment {
    user_id: number | string | null;
    service_provider_id: number | null;
    is_service_provider_responsible: boolean;
  }

  export interface CreatePayload {
    name: string;
    description: string;
    next_due_on: string | Date | null;
    is_singleton: boolean;
    repeat_interval: number;
    repeat_type: string;
    group_id: number | string | null;
    document_id: number | string | null;
    category_id?: number | string | null;
    category_attributes?: { name: string };
    keeper_id?: number | null;
    service_provider_id?: number | null;
    is_service_provider_responsible?: boolean;
    status?: string;
  }

  export interface CreateForm {
    name: string;
    description: string;
    next_due_on: string | Date | null;
    is_singleton: boolean;
    repeat_interval: number;
    repeat_type: string;
    group_id: number | string | null;
    document_id: number | string | null;
    category_id: number | string | null;
    new_category_name: string;
    responsible: string | null;
  }

  // documents

  export interface Document {
    id: number;
    name: string;
    kind?: string;
    category_id?: number | null;
    finalized_document_name?: string | null;
    finalized_document?: string | FileRef | null;
    is_singleton?: boolean | number;
    repeat_interval?: number | null;
    repeat_type?: string | null;
    document_common_template_id?: number | null;
  }

  export interface DocumentsResponse {
    documents?: Document[];
    document?: Document;
    meta?: ListMeta;
  }

  export interface DocumentDraft {
    name?: string;
    kind?: string;
    document_common_template_id?: number | null;
    finalized_document?: string | FileRef | null;
    finalized_document_name?: string | null;
    file_name?: string | null;
    attachment_required?: boolean;
    is_singleton?: number | boolean;
    repeat_interval?: number;
    repeat_type?: string;
    category_id?: number | null;
  }

  export interface CategoriesResponse {
    categories?: Option[];
  }

  export interface TemplatesResponse {
    // `/api/v1/common_templates?flat_list=true` returns `{ items: [...] }`,
    // not `{ templates: [...] }`.
    items?: Option[];
  }

  // wizard

  export type DocumentMode = 'existing' | 'upload' | 'template';

  export interface WizardStep {
    hasPickedServiceProvider?: boolean;
    keeper_id?: number | null;
    service_provider_id?: number | null;
    isCreatingServiceProvider?: boolean;
    group_filter_ids?: number[];
    group_ids?: number[];
    mode?: DocumentMode | null;
    document_id?: number | null;
    document?: DocumentDraft | null;
    name?: string;
    description?: string;
    next_due_on?: string | Date | null;
    is_singleton?: boolean | number;
    repeat_interval?: number | string;
    repeat_type?: string;
    category_id?: number | null;
    new_category_name?: string;
    creatingCategory?: boolean;
  }

  export interface KeeperUser {
    id: number;
    name: string;
    personnel_number?: string | null;
    group_ids?: Array<number | string>;
  }

  export interface UsersResponse {
    users?: KeeperUser[];
  }

  export interface ServiceProviderOption {
    id: number;
    name: string;
  }

  export interface ServiceProvidersResponse {
    service_providers?: ServiceProviderOption[];
  }

  export interface ServiceProviderResponse {
    id?: number;
    name?: string;
    service_provider?: ServiceProviderOption;
  }
}
